import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, IndianRupee, Droplets, Truck } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { api } from "../lib/api";

const VALUES = [
  {
    icon: IndianRupee,
    title: "Luxury at an affordable price",
    text: "We sell directly from the atelier — no counters, no campaigns, no velvet boxes. Every rupee goes into the juice, not the marketing.",
  },
  {
    icon: Leaf,
    title: "Raw materials, named",
    text: "Mysore sandalwood, Kannauj attar, Calabrian bergamot — sourced from growers we know personally. No synthetic fillers.",
  },
  {
    icon: Droplets,
    title: "Hand-decanted in 30ml",
    text: "Small batches, bottled by hand. A perfume you actually finish — and return to — rather than one that gathers dust half-full.",
  },
  {
    icon: Truck,
    title: "Free shipping PAN India",
    text: "Every order ships free, anywhere in India, with a GST tax invoice in your inbox the moment payment clears.",
  },
];

const About = () => {
  const [best, setBest] = useState([]);

  useEffect(() => {
    api
      .get("/products/best-sellers", { params: { limit: 3 } })
      .then((r) => setBest((r.data || []).filter((p) => p.is_best_seller)))
      .catch(() => setBest([]));
  }, []);

  const heroImg = best[0]?.image_url;
  const sideImg = best[1]?.image_url;

  return (
    <div data-testid="about-page" className="min-h-screen">
      <Header />

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-6 items-end">
        <div className="md:col-span-7">
          <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-6 font-bold">
            About Ambdrop · Composed in India
          </p>
          <h1
            data-testid="about-title"
            className="font-serif-display font-light text-5xl sm:text-6xl md:text-7xl lg:text-[112px] leading-[0.88] tracking-[-0.04em] text-[#F4EAD9]"
          >
            Your <span className="italic text-[#D4AF37]">scent</span>,
            <br />
            your <span className="italic text-[#D4AF37]">story</span>.
          </h1>
          <p className="mt-10 max-w-xl text-base md:text-lg leading-relaxed text-[#B0A496]">
            Ambdrop is a small house of fragrances with one conviction: that fine perfume should be
            a daily pleasure, not an annual indulgence. We compose luxury-grade scents and sell them
            at a price you can wear generously.
          </p>
        </div>
        <div className="md:col-span-5 relative">
          <div className="aspect-[3/4] overflow-hidden bg-[#1F1A17] image-zoom-parent">
            {heroImg && <img data-testid="about-hero-image" src={heroImg} alt={best[0]?.name} className="image-zoom w-full h-full object-cover" />}
          </div>
          {sideImg && (
            <div className="hidden md:block absolute -bottom-10 -left-12 w-44 aspect-square overflow-hidden border border-[#2D2622] bg-[#0F0C0A]">
              <img src={sideImg} alt={best[1]?.name} className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      </section>

      {/* Vision */}
      <section data-testid="about-vision" className="border-y border-[#2D2622] bg-[#0A0807]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-6 font-bold">Our vision</p>
            <h2 className="font-serif-display font-light text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight text-[#F4EAD9]">
              Luxury at an <span className="italic text-[#D4AF37]">affordable</span> price.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-6 text-base md:text-lg leading-relaxed text-[#B0A496]">
            <p>
              The fragrance world had quietly decided that luxury meant expensive. We disagreed. The
              scent is the luxury — the rare oud, the real sandalwood, the bergamot picked in season.
              Everything else is theatre, and theatre is what makes a bottle cost what it does.
            </p>
            <p>
              So Ambdrop removed the theatre. We compose with the same raw materials used by houses
              charging many times more, decant by hand in small batches, and ship straight to your
              door. The result is a fragrance wardrobe you can actually build — a scent for the
              office, one for the party, one for the monsoon — without a second thought about price.
            </p>
            <p className="text-[#F4EAD9]">
              Because a perfume is not a trophy. It is the invisible part of how you tell your story —
              every single morning.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-14 font-bold">What we stand for</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-14">
          {VALUES.map((v, i) => (
            <div key={v.title} data-testid={`about-value-${i}`} className="border-t border-[#2D2622] pt-6">
              <v.icon size={20} strokeWidth={1.2} className="text-[#D4AF37]" />
              <h3 className="mt-5 font-serif-display text-2xl md:text-3xl leading-tight text-[#F4EAD9]">{v.title}</h3>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-[#B0A496]">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Best sellers strip */}
      {best.length > 0 && (
        <section data-testid="about-best-sellers" className="border-t border-[#2D2622]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
            <div className="flex items-end justify-between mb-14 gap-6">
              <div>
                <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-4 font-bold">Start here</p>
                <h2 className="font-serif-display font-light text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight text-[#F4EAD9]">
                  The flacons people <span className="italic">return</span> for.
                </h2>
              </div>
              <Link
                data-testid="about-view-all"
                to="/shop?best=1"
                className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-overline uppercase text-[#F4EAD9] editorial-link font-bold"
              >
                All best sellers <ArrowUpRight size={14} strokeWidth={1.2} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {best.map((p, idx) => (
                <ProductCard key={p.id} product={p} index={idx} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Closing CTA */}
      <section className="border-t border-[#2D2622] bg-[#0A0807]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 text-center">
          <p className="font-serif-display font-light text-4xl sm:text-5xl md:text-6xl leading-[0.95] text-[#F4EAD9]">
            Your scent, <span className="italic text-[#D4AF37]">your story</span>.
          </p>
          <Link
            data-testid="about-shop-cta"
            to="/shop"
            className="mt-10 inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#B8941F] text-[#15110F] px-8 py-4 text-[11px] tracking-overline uppercase font-bold transition-colors"
          >
            Begin yours <ArrowUpRight size={16} strokeWidth={1.2} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
